import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types/Select';
import { makeAutoObservable, runInAction } from 'mobx';
import { applyBrokerCart, getAvailableBrokerCarts } from 'api/operations/cartServiceV2';
import { getCartItemNamesByProductLineId, CartItemNamesByProductLineId } from 'global/services-helpers/cartService';
import { BrokerCartInput, Cart } from 'types/cartServiceV2';
import ModalDelegate from 'store/general/modalDelegate';
import { updateGroup } from 'api/operations/groupService';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';
import { initiateProposal } from 'api/operations/enrollmentService';
import { InitiateType } from 'types/enrollmentService';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { ProposalExperiencePayload } from './types';

export default class FavoriteProposalStepController {
  private _carts: Cart[] = [];
  private _isActionLoading = true;
  private _initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  private _getPayload: () => ProposalExperiencePayload;
  public redirectModalDelegate = new ModalDelegate();

  public form: FormController<{
    cartId: FormControl<string>;
  }>;

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get isValid(): boolean {
    return this.form.basicFieldsValidationsPassed && !this.isActionLoading;
  }

  public get groupId(): string {
    return this._initiateQuoteFlowDelegate.domainData.groupId;
  }

  public get favoriteProposalOptions(): OptionType[] {
    return this._carts.map((cart) => ({
      value: cart.id,
      label: cart.cartName,
    }));
  }

  public get cartItemNamesByProductLineId(): CartItemNamesByProductLineId[] {
    if (!this._selectedCart) {
      return [];
    }

    return getCartItemNamesByProductLineId(this._selectedCart);
  }

  private get _selectedCart(): Cart | null {
    const selectedId = this.form.fields.cartId.value;
    return this._carts.find((cart) => cart.id === selectedId) ?? null;
  }

  private get _brokerCartInput(): BrokerCartInput {
    const { effectiveDate, zipCode, state, county, sic4 } = this._getPayload().browsingCriteria;

    return {
      effectiveDate,
      zipCode,
      state,
      county,
      ...(sic4 ? { sic4 } : {}),
    };
  }

  public async fetchFavoriteProposals(): Promise<void> {
    this._isActionLoading = true;
    const carts = await getAvailableBrokerCarts(this._brokerCartInput);

    runInAction(() => {
      this._carts = carts.filter((cart) => cart.rateSegment === 'GROUP' && cart.cartItems.length > 0);
      this._isActionLoading = false;
    });
  }

  public resetStep(): void {
    this.form.resetForm();
    this._carts = [];
  }

  // Create a quote based on the selected favorite proposal
  public async completeStep(): Promise<void> {
    const {
      proposalName,
      browsingCriteria: { effectiveDate },
    } = this._getPayload();
    const selectedCartId = this.form.fields.cartId.value;
    const groupId = this.groupId;

    const newCart = await applyBrokerCart(selectedCartId, this._brokerCartInput, groupId, proposalName);

    await runInAction(async () => {
      const [period] = await Promise.all([
        initiateProposal(
          {
            groupId,
            cartName: proposalName,
            cartId: newCart.id,
            effectiveDate,
            productLines: [],
          },
          'GROUP',
          InitiateType.REGULAR,
        ),
        updateGroup(buildInputGroupFromGroup(this._initiateQuoteFlowDelegate.groupModule.group)),
      ]);

      runInAction(() => {
        const periodId = period.data?.initiateProposal?.id || '';

        if (newCart.id) {
          redirectToRoute(RouteV2Id.QUOTE_CHECKLIST, {
            groupId,
            cartId: newCart.id,
            periodId,
          });
        }
      });
    });
  }

  constructor(initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate, getPayload: () => ProposalExperiencePayload) {
    this._initiateQuoteFlowDelegate = initiateQuoteFlowDelegate;
    this._getPayload = getPayload;
    this.form = FormController.create(
      {
        cartId: new FormControl<string>('', { optional: false }),
      },
      {},
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
